import { BISHOP, type Color, FLAG_CAPTURE, TYPE_MASK, colorBit, encodeMove, moveFrom, moveTo, other } from '../board.ts';
import { DIAGONAL } from '../pieces.ts';
import type { MoveContext } from '../rules.ts';
import type { Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 3 (draft). Bishops can bank off the edge of the board like a pool ball: when a
// bishop's diagonal runs into the edge with nothing in the way (the edge square included),
// it may carry on from that square at a right angle. One bounce per move, never off a
// corner. Bounce moves capture and give check like any other, so the king has to watch
// the rails. Queens don't bounce.

/**
 * PATHS[sq][d]: walking diagonal `d` from `sq`, the squares up to the edge (`ray`, the last
 * one being where the bounce happens) and the squares after bouncing (`after`). `after` is
 * empty when the ray ends in a corner or `sq` is already on the edge in that direction.
 */
interface Path {
  ray: number[];
  after: number[];
}

function walk(f: number, r: number, df: number, dr: number): number[] {
  const out: number[] = [];
  f += df;
  r += dr;
  while (f >= 0 && f < 8 && r >= 0 && r < 8) {
    out.push(r * 8 + f);
    f += df;
    r += dr;
  }
  return out;
}

const PATHS: Path[][] = [];
for (let sq = 0; sq < 64; sq++) {
  PATHS[sq] = DIAGONAL.map(([df, dr]) => {
    const ray = walk(sq & 7, sq >> 3, df, dr);
    if (!ray.length) return { ray, after: [] };
    const edge = ray[ray.length - 1];
    const ef = edge & 7;
    const er = edge >> 3;
    const fileEdge = ef + df < 0 || ef + df > 7;
    const rankEdge = er + dr < 0 || er + dr > 7;
    if (fileEdge && rankEdge) return { ray, after: [] };
    return { ray, after: walk(ef, er, fileEdge ? -df : df, rankEdge ? -dr : dr) };
  });
}

/** Squares a bishop on `from` reaches only by bouncing (empty or capturable), deduplicated. */
export function bounceTargets(board: Int8Array, from: number, color: Color): number[] {
  const own = colorBit(color);
  const themBit = colorBit(other(color));
  // Squares already reachable straight (and the bishop's own square) don't count twice.
  let lo = 0;
  let hi = 0;
  const mark = (s: number) => {
    if (s < 32) lo |= 1 << s;
    else hi |= 1 << (s - 32);
  };
  const marked = (s: number) => (s < 32 ? lo & (1 << s) : hi & (1 << (s - 32))) !== 0;
  mark(from);
  const paths = PATHS[from];
  for (let d = 0; d < 4; d++) {
    const ray = paths[d].ray;
    for (let i = 0; i < ray.length; i++) {
      const q = board[ray[i]];
      if (q === 0 || q & themBit) mark(ray[i]);
      if (q !== 0) break;
    }
  }
  const out: number[] = [];
  for (let d = 0; d < 4; d++) {
    const { ray, after } = paths[d];
    if (!after.length || !clear(board, ray)) continue;
    for (let i = 0; i < after.length; i++) {
      const to = after[i];
      const q = board[to];
      if (q !== 0 && (q & own || !(q & themBit))) break;
      if (!marked(to)) {
        mark(to);
        out.push(to);
      }
      if (q !== 0) break;
    }
  }
  return out;
}

function clear(board: Int8Array, squares: number[]): boolean {
  for (let i = 0; i < squares.length; i++) if (board[squares[i]] !== 0) return false;
  return true;
}

/** Whether a bishop on `from` reaches `to` by a bounce (and not straight). */
export function isBounce(from: number, to: number): boolean {
  return Math.abs((from & 7) - (to & 7)) !== Math.abs((from >> 3) - (to >> 3));
}

/** The square a bounce move from `from` to `to` banks off, or -1. */
export function bounceSquare(board: Int8Array, from: number, to: number): number {
  for (const { ray, after } of PATHS[from]) {
    if (after.includes(to) && clear(board, ray)) return ray[ray.length - 1];
  }
  return -1;
}

/** Bishops of `by` attacking `sq` off the edge, with the square each one banks off. */
export function bounceAttackers(board: Int8Array, sq: number, by: Color): { from: number; via: number }[] {
  const bishop = BISHOP | colorBit(by);
  const out: { from: number; via: number }[] = [];
  for (const { ray, after } of PATHS[sq]) {
    if (!after.length || !clear(board, ray)) continue;
    for (let i = 0; i < after.length; i++) {
      const q = board[after[i]];
      if (q === 0) continue;
      if (q === bishop) out.push({ from: after[i], via: ray[ray.length - 1] });
      break;
    }
  }
  return out;
}

export const bounce: Variant = {
  id: 'bounce',
  info: {
    name: 'Bounce Chess',
    but: 'bishops bounce off the walls',
    tagline: 'Bishops bank off the edge of the board like a pool ball. Check the rails.',
    summary: 'A bishop that runs into the edge of the board can **bounce** off it once and keep going. Bounce shots capture and give check.',
    rules: [
      'A bishop whose diagonal reaches the edge of the board can bounce off it once and keep sliding at a right angle.',
      'The path up to and including the edge square must be empty. No bouncing off corners.',
      'Bounce moves capture and give check like normal moves, so your king isn’t safe behind a pawn wall.',
      'Only bishops bounce. Everything else is normal chess.',
      'Checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#a98bff',
    emoji: '🎱',
    bots: ['Bumper', 'Rebound', 'Trick Shot', 'Bank Shot', 'Pool Shark'],
  },
  startFen: STANDARD_FEN,
  // A bounce keeps a bishop on its colour, so the usual dead positions stay dead.
  insufficientMaterial: true,

  generate(pos, out, capturesOnly) {
    const b = pos.board;
    const us = pos.turn;
    const bishop = BISHOP | colorBit(us);
    for (let sq = 0; sq < 64; sq++) {
      if (b[sq] !== bishop) continue;
      for (const to of bounceTargets(b, sq, us)) {
        if (b[to] !== 0) out.push(encodeMove(sq, to, 0, FLAG_CAPTURE));
        else if (!capturesOnly) out.push(encodeMove(sq, to));
      }
    }
  },

  afterMove(ctx: MoveContext) {
    // Only the UI needs to know where a bounce banked (for its effect and sound).
    if (!ctx.events || (ctx.piece & TYPE_MASK) !== BISHOP) return;
    const from = moveFrom(ctx.move);
    const to = moveTo(ctx.move);
    if (isBounce(from, to)) ctx.fx('bounce', bounceSquare(ctx.prev.board, from, to));
  },

  attacks(board, sq, by) {
    // Walk back from the target: out to the edge over empty squares, bounce, then the first
    // piece met must be a bishop of `by`. Straight attacks are already in the piece tables.
    const bishop = BISHOP | colorBit(by);
    const paths = PATHS[sq];
    for (let d = 0; d < 4; d++) {
      const { ray, after } = paths[d];
      if (!after.length || !clear(board, ray)) continue;
      for (let i = 0; i < after.length; i++) {
        const q = board[after[i]];
        if (q === 0) continue;
        if (q === bishop) return true;
        break;
      }
    }
    return false;
  },
};
